// src/pages/vendas/PDV2.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import "./PDV2.css";
import { Nota } from '../../models/vendas/Nota';
import { NotaItem } from '../../models/vendas/NotaItem';
import ModalFinalizarVenda from './ModalFinalizarVenda';
import { useMessage } from "../../contexts/geral/MessageContext.js";
import { CaixaMovProvider } from "../../contexts/caixas/CaixaMovContext.js";
import { useYesNo } from "../../contexts/geral/YesNoContext.js";
import { useConfigModulo } from "../../contexts/administrativo/ConfigModuloContext.js";
import { useConfigPedido } from "../../contexts/pedidos/ConfigPedidoContext.js";
import ModalPedidosAnotaAI from "./ModalPedidosAnotaAI.js";
import { usePDV } from "../../contexts/vendas/PDVContext.js";
import { useCaixa } from "../../contexts/caixas/CaixaContext.js";
import { useNota } from "../../contexts/vendas/NotaContext.js";
import ProdutoSearchMultiplos from "../produtos/produtos/ProdutoSearchMultiplos.js";
import { useProduto } from "../../contexts/produtos/ProdutoContext.js";
import { useNotaPag } from "../../contexts/vendas/NotaPagContext.js";
import { useToken } from "../../contexts/geral/TokenContext.js";
import Input from '../../components/input/Input.js';
import { atualizarNotaFinalizacaoPDV, calcularTotalItensPDV, criarNotaFinalizacaoPDV, obterItemSemValor } from "../../services/vendas/PDVService.js";
import { normalizarTextoProduto, produtoCorrespondeFiltro } from "../../services/produtos/ProdutoFiltroService.js";
import { formatNumeric, parseNumeric } from "../../utils/numberUtils.js";
import { FaMinus, FaPlus } from "react-icons/fa";
import { FiShoppingCart, FiSearch, FiPackage, FiCreditCard, FiTrash2, FiXCircle, FiLock, FiInbox } from "react-icons/fi";

const MINIMO_CARACTERES_AUTOCOMPLETE = 2;
const LIMITE_SUGESTOES_AUTOCOMPLETE = 10;

const PDV2 = () => {    
    
    const [showSearchProduto, setShowSearchProduto] = useState(false);            
    const [showFinalizarVenda, setShowFinalizarVenda] = useState(false);
       
    const [nota, setNota] = useState(new Nota());
    const {itensPDV, setItensPDV, itemPDVSel, setItemPDVSel, barCode, setNumItem, setBarCode, 
        addProduct, adicionarProdutos, showDeleteModal, setShowDeleteModal,
        handleAjustaTotal, handleMudaDescricao, handleQuantidadeChange, handleValorUnChange} = usePDV();
    const {caixaSel, showCaixaMov, setShowCaixaMov, fecharCaixa } = useCaixa();
    const {notaSel} = useNota();
    const { fetchNotaPags } = useNotaPag();
    const { showMessage } = useMessage();
    const { showYesNo } = useYesNo();    
    const {configModuloSel} = useConfigModulo();
    const { configPedido } = useConfigPedido();
    const { produtos } = useProduto();
    const { garantirSessaoAtiva } = useToken();

    const cartItemsRef = useRef(null);
    const cartRef = useRef(null);
    const barCodeInputRef = useRef(null);
    const produtoAutocompleteRef = useRef(null);
    const quantidadeInputRef = useRef(null);
    const vUnComInputRef = useRef(null);
    const focoValorAposBuscaRef = useRef(null);
    const atalhosRef = useRef(null);
    const finalizacaoEmAndamentoRef = useRef(false);
    const confirmacaoEmAndamentoRef = useRef(false);
    const [isFinalizando, setIsFinalizando] = useState(false);
    const [showProdutoAutocomplete, setShowProdutoAutocomplete] = useState(false);
    const [produtoAutocompleteIndex, setProdutoAutocompleteIndex] = useState(-1);
    
    const [isMobile, setIsMobile] = useState(false); // 👈 controla se é celular ou não

    // Detecta se é mobile
    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth <= 768); // pode ajustar o breakpoint
        };
        checkIsMobile();
        window.addEventListener("resize", checkIsMobile);
        return () => window.removeEventListener("resize", checkIsMobile);
    }, []);

    const termoProdutoNormalizado = normalizarTextoProduto(barCode);

    const produtosAutocomplete = useMemo(() => {
        if (termoProdutoNormalizado.length < MINIMO_CARACTERES_AUTOCOMPLETE) {
            return [];
        }

        return produtos
            .filter(produto => (
                !produto?.inativo &&
                !produto?.grupo?.inativo &&
                produtoCorrespondeFiltro(produto?.descricao, termoProdutoNormalizado)
            ))
            .sort((produtoA, produtoB) => {
                const descricaoA = normalizarTextoProduto(produtoA?.descricao);
                const descricaoB = normalizarTextoProduto(produtoB?.descricao);
                const comecaComTermoA = descricaoA.startsWith(termoProdutoNormalizado);
                const comecaComTermoB = descricaoB.startsWith(termoProdutoNormalizado);

                if (comecaComTermoA !== comecaComTermoB) {
                    return comecaComTermoA ? -1 : 1;
                }

                return descricaoA.localeCompare(descricaoB, 'pt-BR');
            });
    }, [produtos, termoProdutoNormalizado]);

    const sugestoesProduto = useMemo(
        () => produtosAutocomplete.slice(0, LIMITE_SUGESTOES_AUTOCOMPLETE),
        [produtosAutocomplete]
    );
    const exibirProdutoAutocomplete = showProdutoAutocomplete &&
        termoProdutoNormalizado.length >= MINIMO_CARACTERES_AUTOCOMPLETE;

    useEffect(() => {
        if (!exibirProdutoAutocomplete || sugestoesProduto.length === 0) {
            setProdutoAutocompleteIndex(-1);
            return;
        }

        setProdutoAutocompleteIndex(0);
    }, [barCode, exibirProdutoAutocomplete, sugestoesProduto.length]);

    useEffect(() => {
        const handleClickForaAutocomplete = (event) => {
            if (produtoAutocompleteRef.current && !produtoAutocompleteRef.current.contains(event.target)) {
                setShowProdutoAutocomplete(false);
            }
        };

        document.addEventListener('mousedown', handleClickForaAutocomplete);
        return () => document.removeEventListener('mousedown', handleClickForaAutocomplete);
    }, []);

    useEffect(() => {
        if (!exibirProdutoAutocomplete || produtoAutocompleteIndex < 0) {
            return;
        }

        document
            .getElementById(`pdv-produto-sugestao-${sugestoesProduto[produtoAutocompleteIndex]?.id}`)
            ?.scrollIntoView?.({ block: 'nearest' });
    }, [exibirProdutoAutocomplete, produtoAutocompleteIndex, sugestoesProduto]);

    useEffect(() => {
        if (notaSel.id!==0) {
            setNota(notaSel); // seta a nota completa
            if (notaSel.notaItens?.length > 0) {
                const maiorNumItem = notaSel.notaItens.reduce((maiorItem, notaItem) => {
                    const numItemAtual = Number(notaItem?.item) || 1;
                    return numItemAtual > maiorItem ? numItemAtual : maiorItem;
                }, 0);

                setItensPDV(notaSel.notaItens); //  array completo
                setItemPDVSel(notaSel.notaItens[notaSel.notaItens.length - 1]); // seleciona o último item
                setNumItem(maiorNumItem + 1); // ajusta o numItem para o próximo item
            }
            console.log("acesso tela vendas");
            
        }
    }, []);


    // Efeito para ajustar a altura do carrinho
    // A altura acompanha o layout por CSS, sem tirar o foco do campo ao redimensionar.
    useEffect(() => {
        barCodeInputRef.current?.focus();
    }, []);

    const executarComSessaoAtiva = (acao) => {
        try {
            const resultado = garantirSessaoAtiva();

            if (resultado && typeof resultado.then === 'function') {
                return resultado.then(acao).catch(() => false);
            }

            return acao();
        } catch (_) {
            return false;
        }
    };

    const incluirProduto = (codigoProduto) => {
        const itemSelecionado = addProduct(codigoProduto);

        if (!itemSelecionado) {
            return null;
        }

        setShowProdutoAutocomplete(false);
        setProdutoAutocompleteIndex(-1);

        setTimeout(() => {
            if (itemSelecionado.vProd > 0) {
                barCodeInputRef.current?.focus();
            } else {
                vUnComInputRef.current?.focus();
                vUnComInputRef.current?.select();
            }
        }, 0);

        return itemSelecionado;
    };

    const incluirProdutoNoPDV = (codigoProduto) => {
        if (itensPDV.length > 0) {
            return incluirProduto(codigoProduto);
        }

        return executarComSessaoAtiva(() => incluirProduto(codigoProduto));
    };

    const handleSelectProdutoAutocomplete = (produtoSelecionado) => {
        incluirProdutoNoPDV(produtoSelecionado.id.toString());
    };

    const handleChangeProduto = (e) => {
        const novoValor = e.target.value;
        setBarCode(novoValor);
        setShowProdutoAutocomplete(normalizarTextoProduto(novoValor).length >= MINIMO_CARACTERES_AUTOCOMPLETE);
    };

    const handleKeyDownProd = (e) => {
        if (e.key === 'ArrowDown' && sugestoesProduto.length > 0) {
            e.preventDefault();
            setShowProdutoAutocomplete(true);
            setProdutoAutocompleteIndex(indexAtual => (
                indexAtual < 0 ? 0 : (indexAtual + 1) % sugestoesProduto.length
            ));
            return;
        }

        if (e.key === 'ArrowUp' && sugestoesProduto.length > 0) {
            e.preventDefault();
            setShowProdutoAutocomplete(true);
            setProdutoAutocompleteIndex(indexAtual => (
                indexAtual <= 0 ? sugestoesProduto.length - 1 : indexAtual - 1
            ));
            return;
        }

        if (e.key === 'Escape' && exibirProdutoAutocomplete) {
            e.preventDefault();
            setShowProdutoAutocomplete(false);
            setProdutoAutocompleteIndex(-1);
            return;
        }

        if (e.key === "Enter" && e.target.value.trim() !== "") {
            e.preventDefault();
            const valorDigitado = e.target.value.trim();
            const produtoPorCodigo = produtos.find(produto => (
                String(produto?.id) === valorDigitado || String(produto?.ean || '') === valorDigitado
            ));
            const produtoSugerido = exibirProdutoAutocomplete
                ? sugestoesProduto[produtoAutocompleteIndex]
                : null;

            if (produtoPorCodigo) {
                incluirProdutoNoPDV(valorDigitado);
            } else if (produtoSugerido) {
                handleSelectProdutoAutocomplete(produtoSugerido);
            } else {
                incluirProdutoNoPDV(valorDigitado);
            }
        }
    };

    const handleCancelarVenda = async () => {
        if (confirmacaoEmAndamentoRef.current) return;
        confirmacaoEmAndamentoRef.current = true;
        try {
            const resposta = await showYesNo("Deseja cancelar a venda?");
            if (resposta) {
                setNota(new Nota());//limpa a nota também
                setItensPDV([]);
                setItemPDVSel(new NotaItem());
                setNumItem(1);
                setBarCode('');
                setShowProdutoAutocomplete(false);
            }
        } finally {
            confirmacaoEmAndamentoRef.current = false;
            setTimeout(() => barCodeInputRef.current?.focus(), 0);
        }
    };

    const handleSetFocusCod = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            barCodeInputRef.current?.focus();
        }
    };

    const adicionarSelecaoProdutos = (selecao) => {
        const resultado = adicionarProdutos(selecao);
        if (resultado === false) return false;
        focoValorAposBuscaRef.current = Boolean(resultado.selecionado && parseNumeric(resultado.selecionado.vUnCom) <= 0);
        setShowProdutoAutocomplete(false);
        setProdutoAutocompleteIndex(-1);
        return true;
    };

    const handleSelectProdutos = (selecao) => {
        if (itensPDV.length > 0) {
            return adicionarSelecaoProdutos(selecao);
        }

        return executarComSessaoAtiva(() => adicionarSelecaoProdutos(selecao));
    };

    const fecharBuscaProdutos = () => {
        setShowSearchProduto(false);
        const focarValor = focoValorAposBuscaRef.current ?? Boolean(itemPDVSel?.item && parseNumeric(itemPDVSel.vUnCom) <= 0);
        focoValorAposBuscaRef.current = null;
        setTimeout(() => {
            if (focarValor) {
                vUnComInputRef.current?.focus();
                vUnComInputRef.current?.select();
            } else {
                barCodeInputRef.current?.focus();
            }
        }, 0);
    };

    const handleAjustaQuantidadePorBotao = async (incremento) => {
        if (!itemPDVSel?.item) {
            return;
        }

        const quantidadeAtual = parseNumeric(itemPDVSel.qtdCom);
        if (incremento < 0 && quantidadeAtual === 1) {
            const resposta = await showYesNo("Deseja excluir o item selecionado?");
            if (!resposta) {
                quantidadeInputRef.current?.focus();
                return;
            }

            const indiceItemSelecionado = itensPDV.findIndex(item => item.item === itemPDVSel.item);
            const novosItens = itensPDV.filter(item => item.item !== itemPDVSel.item);
            const novoItemSelecionado = novosItens[Math.min(indiceItemSelecionado, novosItens.length - 1)];

            setItensPDV(novosItens);
            setItemPDVSel(novoItemSelecionado || new NotaItem());
            setTimeout(() => {
                if (novoItemSelecionado) {
                    quantidadeInputRef.current?.focus();
                } else {
                    barCodeInputRef.current?.focus();
                }
            }, 0);
            return;
        }

        const novaQuantidade = Math.max(1, quantidadeAtual + incremento);
        handleQuantidadeChange({ target: { value: novaQuantidade } });
        quantidadeInputRef.current?.focus();
    };      

    useEffect(() => {
        if (cartItemsRef.current) {
            cartItemsRef.current.scrollTop = cartItemsRef.current.scrollHeight;
        }
    }, [itensPDV.length]);

    const total = calcularTotalItensPDV(itensPDV);

    const finalizarVenda = async () => {
        if (finalizacaoEmAndamentoRef.current || showFinalizarVenda) return;
        finalizacaoEmAndamentoRef.current = true;
        setIsFinalizando(true);
        try {
            try {
                await garantirSessaoAtiva();
            } catch (_) {
                return;
            }

            if (itensPDV.length===0)
            {
                showMessage("Informe pelo menos um item antes de finalizar a venda!", 'warning');
                return;
            }

            const itemSemValor = obterItemSemValor(itensPDV);

            if (itemSemValor) {
                showMessage(`O item ${itemSemValor.item} "${itemSemValor.xProd || 'sem nome'}" está com valor zerado!`, 'warning');
                return;
            }
            if (!nota.id)
            {
                setNota(criarNotaFinalizacaoPDV({ total, itensPDV }));
            } else {
                let resultNotaPags = await fetchNotaPags(nota.id); // busca os pagamentos atualizados da nota antes de abrir o modal
                setNota(atualizarNotaFinalizacaoPDV({ nota, total, notaPags: resultNotaPags || [], itensPDV }));
            }

            setShowProdutoAutocomplete(false);
            setShowFinalizarVenda(true);
        } catch (_) {
            showMessage('Não foi possível preparar a finalização da venda. Tente novamente.', 'danger');
        } finally {
            finalizacaoEmAndamentoRef.current = false;
            setIsFinalizando(false);
        }
    };

    const handleCancelVenda = () => {
        // setNota(new Nota());        
        // setItensPDV(itensPDV.map(i => ({
        //     ...i,
        //     id: 0,
        //     notaId: 0
        // })));

        // setItemPDVSel({...itemPDVSel, id:0, notaId:0});

        setShowFinalizarVenda(false);
        setTimeout(() => barCodeInputRef.current?.focus(), 0);
    };

    const handleConfirmVenda = () => {
        setItensPDV([]);
        setNota(new Nota());
        setItemPDVSel(new NotaItem());
        setShowFinalizarVenda(false);
        setNumItem(1);
        setBarCode('');
        setTimeout(() => barCodeInputRef.current?.focus(), 0);
    };

    // Efeito para monitorar o fechamento do modal CaixaMovLan
    useEffect(() => {
        if (!showCaixaMov && barCodeInputRef.current) {
            barCodeInputRef.current.focus();
        }
    }, [showCaixaMov]);
    // Efeito para monitorar o fechamento do modal delete itens
    useEffect(() => {
        if (!showDeleteModal && barCodeInputRef.current) {
            barCodeInputRef.current.focus();
        }
    }, [showDeleteModal]);


    //Anota AI
    const [showPedidosAnotaAI, setShowPedidosAnotaAI] = useState(false);

    const abrirBuscaProdutos = () => {
        setShowProdutoAutocomplete(false);
        setShowSearchProduto(true);
    };

    atalhosRef.current = {
        bloqueado: showSearchProduto || showFinalizarVenda || showDeleteModal || showCaixaMov || showPedidosAnotaAI,
        F2: abrirBuscaProdutos,
        F3: () => setShowDeleteModal(true),
        F8: handleCancelarVenda,
        F9: finalizarVenda,
        caixa: () => setShowCaixaMov(true)
    };

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.defaultPrevented || event.isComposing || event.ctrlKey || event.metaKey) return;
            const atalhos = atalhosRef.current;
            if (atalhos.bloqueado || finalizacaoEmAndamentoRef.current || confirmacaoEmAndamentoRef.current ||
                document.querySelector('.modal.show, [role="dialog"][aria-modal="true"]')) return;

            const acao = event.altKey
                ? (event.key.toLowerCase() === 'c' ? atalhos.caixa : null)
                : atalhos[event.key];
            if (!acao) return;
            event.preventDefault();
            if (!event.repeat) acao();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        if (!showPedidosAnotaAI) {
            setTimeout(() => barCodeInputRef.current?.focus(), 0);
        }
    }, [showPedidosAnotaAI]);

    return (
        <CaixaMovProvider>
            <div className="container-venda" data-mobile={isMobile}>
                <div className="header-venda">
                    <div className="pdv-heading">
                        <span className="pdv-heading-icon"><FiShoppingCart aria-hidden="true" /></span>
                        <div>
                            <h2>Frente de caixa</h2>
                            <span>{configModuloSel?.nomeEstabelecimento?configModuloSel?.nomeEstabelecimento:"INFOMAIO SISTEMAS GERENCIAIS"}</span>
                        </div>
                    </div>
                    <div className="pdv-header-actions">
                        {caixaSel?.aberto && <button type="button"
                            className="caixa-register-status pdv-fechar-caixa"
                            onClick={fecharCaixa}
                        >
                            <FiLock aria-hidden="true" /> Fechar Caixa
                        </button>}
                        <button type="button"
                            className={`caixa-register-status ${caixaSel?.aberto ? "open" : "closed"}`}
                            onClick={() => setShowCaixaMov(true)}
                        >
                            <span className="pdv-caixa-indicador" aria-hidden="true" />
                            {caixaSel?.descricao ?? 'Caixa'}: {caixaSel?.aberto ? "Aberto" : "Fechado"}
                        </button>
                        {/* Só renderiza se tiver pedidos */}
                        {configPedido?.tokenAnotaAi && (<button type="button"
                            className="btn-anota-ai"
                            onClick={() => setShowPedidosAnotaAI(true)}
                        >
                            <FiInbox aria-hidden="true" /> Pedidos Anota Aí
                        </button>)}
                        
                    </div>
                </div>
                <div className="content-venda">
                    <div className="inputs-and-buttons">
                        <div className="pdv-panel-heading">
                            <h3><FiPackage aria-hidden="true" /> Produto</h3>
                            {itemPDVSel.item > 0 && <span>Item {itemPDVSel.item}</span>}
                        </div>
                        <div className="product-input product-autocomplete" ref={produtoAutocompleteRef}>
                            <label htmlFor="pdv-produto-input">Código, código de barras ou produto:</label>
                            <div className="pdv-busca-controle">
                            <input
                                id="pdv-produto-input"
                                type="text"
                                placeholder="Código, barras ou descrição"
                                value={barCode}
                                disabled={isFinalizando}
                                autoComplete="off"
                                spellCheck={false}
                                onChange={handleChangeProduto}
                                onKeyDown={handleKeyDownProd}
                                onFocus={() => setShowProdutoAutocomplete(
                                    termoProdutoNormalizado.length >= MINIMO_CARACTERES_AUTOCOMPLETE
                                )}
                                onBlur={(event) => {
                                    if (!produtoAutocompleteRef.current?.contains(event.relatedTarget)) {
                                        setShowProdutoAutocomplete(false);
                                    }
                                }}
                                onDoubleClick={() => {
                                    setShowProdutoAutocomplete(false);
                                    setShowSearchProduto(true);
                                }} // 👈 novo evento
                                ref={barCodeInputRef}
                                role="combobox"
                                aria-autocomplete="list"
                                aria-expanded={exibirProdutoAutocomplete}
                                aria-controls="pdv-produtos-sugestoes"
                                aria-activedescendant={
                                    exibirProdutoAutocomplete && produtoAutocompleteIndex >= 0
                                        ? `pdv-produto-sugestao-${sugestoesProduto[produtoAutocompleteIndex]?.id}`
                                        : undefined
                                }
                            />
                            <button type="button" className="pdv-buscar-produto" aria-label="Pesquisar produtos" title="Pesquisar produtos (F2)" disabled={isFinalizando} onClick={abrirBuscaProdutos}><FiSearch aria-hidden="true" /></button>
                            </div>
                            <small className="pdv-busca-ajuda">Enter para incluir · F2 para pesquisar</small>
                            {exibirProdutoAutocomplete && (
                                <div className="produto-sugestoes" id="pdv-produtos-sugestoes" role="listbox">
                                    {sugestoesProduto.length > 0 ? (
                                        <>
                                            <div className="produto-sugestoes-lista">
                                                {sugestoesProduto.map((produto, index) => {
                                                    const unidade = produto.unTrib || produto.unMedida?.sigla || 'UN';
                                                    const selecionado = index === produtoAutocompleteIndex;

                                                    return (
                                                        <button
                                                            id={`pdv-produto-sugestao-${produto.id}`}
                                                            key={produto.id}
                                                            type="button"
                                                            role="option"
                                                            aria-selected={selecionado}
                                                            className={`produto-sugestao ${selecionado ? 'selecionada' : ''}`}
                                                            onMouseEnter={() => setProdutoAutocompleteIndex(index)}
                                                            onMouseDown={(event) => event.preventDefault()}
                                                            onClick={() => handleSelectProdutoAutocomplete(produto)}
                                                        >
                                                            <span className="produto-sugestao-descricao">{produto.descricao}</span>
                                                            <span className="produto-sugestao-detalhes">
                                                                <span>Cód. {produto.id}</span>
                                                                {produto.ean && <span>EAN {produto.ean}</span>}
                                                                <span>{unidade}</span>
                                                                <strong>R$ {formatNumeric(produto.preco)}</strong>
                                                            </span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            {produtosAutocomplete.length > LIMITE_SUGESTOES_AUTOCOMPLETE && (
                                                <div className="produto-sugestoes-rodape">
                                                    Mais resultados encontrados. Digite mais para refinar a busca.
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="produto-sugestoes-vazio" role="status">
                                            Nenhum produto ativo encontrado.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="product-input pdv-descricao-produto">
                            <Input
                                label="Descrição do Produto:"
                                size="100%"
                                id="pdv-descricao"
                                type="text"
                                title={itemPDVSel.xProd || ''}
                                value={itemPDVSel.xProd || ''}
                                onChange={(e) => setItemPDVSel({ ...itemPDVSel, xProd: e.target.value })}
                                onBlur={handleMudaDescricao}
                                onKeyDown={handleSetFocusCod}
                                disabled={!itemPDVSel.item || isFinalizando}
                                readOnly={!itemPDVSel.produto?.alteraDescricao}
                                tabIndex={!itemPDVSel.produto?.alteraDescricao ? -1 : undefined}
                            />
                        </div>
                        <div className="product-input pdv-item-campos">
                            <div className="pdv-campo-unidade">
                                <Input
                                    label="Unidade:"
                                    size="100%"
                                    id="pdv-unidade"
                                    type="text"
                                    value={itemPDVSel.unCom || ''}
                                    readOnly
                                    disabled
                                    style={{ textAlign: 'left' }}
                                />
                            </div>
                            <div className="pdv-campo-quantidade">
                                <span className="pdv-campo-label" aria-hidden="true">Quantidade:</span>
                                <div className="quantidade-controle">
                                    <button
                                        type="button"
                                        onClick={() => handleAjustaQuantidadePorBotao(-1)}
                                        className="quantidade-btn"
                                        title="Diminuir quantidade"
                                        aria-label="Diminuir quantidade"
                                        disabled={!itemPDVSel.item || isFinalizando}
                                    >
                                        <FaMinus aria-hidden="true" />
                                    </button>
                                    <Input
                                        label="Quantidade:"
                                        labelHidden
                                        size={80}
                                        type="text"
                                        inputMode="decimal"
                                        disabled={!itemPDVSel.item || isFinalizando}
                                        style={{ textAlign: 'right', minWidth: '76px' }}
                                        value={itemPDVSel.qtdCom || ''}
                                        onChange={handleQuantidadeChange}
                                        onBlur={handleAjustaTotal}
                                        inputRef={quantidadeInputRef}
                                        onKeyDown={handleSetFocusCod}
                                        onFocus={e => e.target.select()}
                                        id="qtde"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => handleAjustaQuantidadePorBotao(1)}
                                        className="quantidade-btn"
                                        title="Aumentar quantidade"
                                        aria-label="Aumentar quantidade"
                                        disabled={!itemPDVSel.item || isFinalizando}
                                    >
                                        <FaPlus aria-hidden="true" />
                                    </button>
                                </div>
                            </div>
                            <div className="pdv-campo-valor">
                                <Input
                                    label="Valor Unitário:"
                                    size="100%"
                                    type="text"
                                    inputMode="decimal"
                                    disabled={!itemPDVSel.item || isFinalizando}
                                    value={itemPDVSel.vUnCom || ''}
                                    onChange={handleValorUnChange}
                                    onBlur={handleAjustaTotal}
                                    inputRef={vUnComInputRef}
                                    readOnly={itemPDVSel.produto?.precoFixo}
                                    tabIndex={itemPDVSel.produto?.precoFixo ? -1 : undefined}
                                    onKeyDown={handleSetFocusCod}
                                    style={{ textAlign: 'right' }}
                                    id="vUn"
                                    onFocus={e => e.target.select()}
                                />
                            </div>
                        </div>
                        <div className="pdv-total-item">
                            <div className="product-input">
                                <span className="pdv-total-item-label">Total do item</span>
                                <output
                                    id="pdv-total-item"
                                    className="total-item"
                                    htmlFor="qtde vUn"
                                    aria-label="Valor Total Item:"
                                >R$ {formatNumeric(itemPDVSel.vProd)}</output>
                            </div>
                        </div>
                    </div>

                    <div className="cart" ref={cartRef}>
                        <div className="pdv-panel-heading">
                            <h3><FiShoppingCart aria-hidden="true" /> Lista de Produtos</h3>
                            <span>{itensPDV.length} {itensPDV.length === 1 ? 'item' : 'itens'}</span>
                        </div>
                        <div className="pdv-cart-scroll" ref={cartItemsRef}>
                        <table className="cart-table" aria-label="Produtos da venda">
                            <thead>
                                <tr>
                                    <th scope="col">Item</th>
                                    <th scope="col">Descrição</th>
                                    <th scope="col">Unid.</th>
                                    <th scope="col">Qtde</th>
                                    <th scope="col">Valor</th>
                                </tr>
                            </thead>
                            <tbody>
                                {itensPDV.map((item, index) => (
                                    <tr
                                        key={item.item} // Use item.item como chave, já que é único
                                        onClick={() => setItemPDVSel(item)}
                                        tabIndex={0}
                                        aria-selected={item.item === itemPDVSel.item}
                                        aria-label={`Item ${item.item}, ${item.xProd}. Selecionar para editar`}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                setItemPDVSel(item);
                                            }
                                        }}
                                        className={
                                            item.item === itemPDVSel.item
                                                ? 'selected-item'
                                                : index % 2 === 0
                                                ? 'odd'
                                                : 'even'
                                        }
                                    >
                                        <td>{item.item}</td>
                                        <td title={item.xProd}>{item.xProd}</td>
                                        <td>{item.unCom}</td>
                                        <td>{String(item.qtdCom).replace('.', ',')}</td>
                                        <td>R$ {formatNumeric(item.vProd)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {itensPDV.length === 0 && <div className="pdv-cart-vazio">
                            <FiShoppingCart aria-hidden="true" />
                            <strong>Comece uma nova venda</strong>
                            <span>Leia o código de barras ou pesquise um produto para incluir.</span>
                        </div>}
                        </div>
                    </div>
                </div>
                <div className="footer-venda">
                    <div className="atalhos">
                        <span className="pdv-atalhos-label">Ações da venda</span>
                        <div className="atalhos-container">
                            <div className="atalhos-options">
                                <button type="button" aria-label="F2 - Pesquisar" disabled={isFinalizando} onClick={abrirBuscaProdutos}>
                                    <FiSearch aria-hidden="true" /> Pesquisar <kbd>F2</kbd>
                                </button>
                                <button type="button" aria-label="F3 - Excluir Item" disabled={isFinalizando} onClick={() => setShowDeleteModal(true)}>
                                    <FiTrash2 aria-hidden="true" /> Excluir item <kbd>F3</kbd>
                                </button>
                                <button type="button" aria-label="F8 - Cancelar Venda" className="cancelar" disabled={isFinalizando} onClick={handleCancelarVenda}>
                                    <FiXCircle aria-hidden="true" /> Cancelar venda <kbd>F8</kbd>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="pdv-concluir">
                        <div className="total-container-venda">
                            <span className="total-label">Total</span>
                            <span className="total-value">R$ {formatNumeric(total)}</span>
                        </div>
                        <button type="button" aria-label="F9 - Finalizar Venda" className="finalizar" disabled={isFinalizando} aria-busy={isFinalizando} onClick={finalizarVenda}>
                            <FiCreditCard aria-hidden="true" /> {isFinalizando ? 'Preparando...' : 'Finalizar venda'} <kbd>F9</kbd>
                        </button>
                    </div>
                </div>

                {/* Aqui só mostra 1 dos dois componentes */}
                {/* A busca múltipla agora atende desktop e celular. */}
                <ProdutoSearchMultiplos
                    show={showSearchProduto}
                    onHide={fecharBuscaProdutos}
                    onSelectProdutos={handleSelectProdutos}
                />
                <ModalFinalizarVenda
                    show={showFinalizarVenda}
                    onHide={handleCancelVenda}
                    nota={nota}
                    setNota={setNota}
                    onConfirm={handleConfirmVenda}
                />                             
                <ModalPedidosAnotaAI
                    show={showPedidosAnotaAI}
                    onClose={() => setShowPedidosAnotaAI(false)}
                    //setValorFrete={setValorFrete}
                    onConfirm={() => {
                        console.log("Confirmar pedidos Anota Aí");
                        setShowPedidosAnotaAI(false);
                    }}
                />
            </div>
        </CaixaMovProvider>
    );
};

export default PDV2;
